import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { createCellCssClassRules, CreateCellDoubleClickHandler, CustomCellDoubleClickAction, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell } from "src/utils/grid/gridStyleUtils";
import { useMST3050_R01Store } from "./Store_MST3050_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { MST3050_R01DetailDto } from "./Dto_MST3050_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";

import { createModuleConfig } from "src/utils/detail-table/detailTableUtils";
import { useMouseContent } from "src/utils/mouse/mouseContent";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useCodeSearch } from "src/hooks/useCodeSearch";
import { getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";

const COLUMN_STATE_KEY = 'MST3050_R01-detail-grid-column-state';

interface Props {
    gridRef: React.RefObject<AgGridReact<MST3050_R01DetailDto> | null>;
	defaultMST3050_R01DetailRow: MST3050_R01DetailDto;
}

export const DetailTable_MST3050_R01 = (props: Props) => {

    // [수정] props에서 defaultMST3050_R01DetailRow를 구조 분해 할당으로 가져옵니다.
    const { gridRef, defaultMST3050_R01DetailRow } = props;

    const { 
            MST3050_R01DetailList, setMST3050_R01DetailList, 
            editMST3050_R01Detail, setEditMST3050_R01Detail,
            selectedMST3050_R01DetailList, setSelectedMST3050_R01DetailList, removeSelectedMST3050_R01DetailList, addSelectedMST3050_R01DetailList, initSelectedMST3050_R01DetailList,
            deletedMST3050_R01DetailList, setDeletedMST3050_R01DetailList, addDeletedMST3050_R01DetailList, removeDeletedMST3050_R01DetailList,
            insertMST3050_R01DetailList, updateMST3050_R01DetailList, removeMST3050_R01DetailList,
            detailColumnState, setDetailColumnState,
            detailScrollTop, setDetailScrollTop,
            originalMST3050_R01DetailList, setOriginalMST3050_R01DetailList,
        } = useMST3050_R01Store();
    const { setCodeSearchModalProps } = useUiuxStore();
    const [clickable, setClickable] = useState(true);
    const { layerList } = useCommonCodeStore();

    const gridWrapperRef = useRef<HTMLDivElement>(null);

    // === DetailTable 내장 핸들러 로직 ===
const { searchExactCode } = useCodeSearch({});

    // 모듈별 설정
    const MST3050R01Config = createModuleConfig.MST3050();

    // 공통 검색 함수를 설정에 주입 (공정코드, 창고코드 카테고리)
    const fieldConfigWithSearch = {
        ...MST3050R01Config.fieldConfig,
        codeSearchFields: {
            ...MST3050R01Config.fieldConfig.codeSearchFields,
            childItemcd: {
                ...MST3050R01Config.fieldConfig.codeSearchFields!.childItemcd,
                searchFunction: (code: string, onSuccess?: (item: any) => void) => 
                    searchExactCode('item', code, onSuccess)
            },
        }
    };

    // 공통 훅 사용
    const detailTableHandlers = useCommonDetailTableHandlers<MST3050_R01DetailDto>({
        detailList: MST3050_R01DetailList,
        updateDetailList: updateMST3050_R01DetailList,
        originalDetailList: originalMST3050_R01DetailList,
        setCodeSearchModalProps,
        gridRef,
        fieldConfig: fieldConfigWithSearch,
        codeSearchConfig: MST3050R01Config.codeSearchConfig,
        calculateAmount: (data: MST3050_R01DetailDto) => data, // 금액 계산 함수 없음
    });

    const {
        getRowId,
        createRowClassRules,
        createCellClassRules,
        paddedGridData,
        renderIndexCell,
    } = useGridSettingContent({
        gridRef,
        key: 'valueIndex',
        gridData: MST3050_R01DetailList,
        defaultGridData: defaultMST3050_R01DetailRow,
        selectedRow: selectedMST3050_R01DetailList,
        originalGridData: originalMST3050_R01DetailList,
        updateRow: (index: number, data: any) => updateMST3050_R01DetailList(index, data),
    });

    const headerNames: Record<string, string> = {
        rowIndex: "",
		coordinateCd: '로케이션',
		coordinateNm: '로케이션명',
        childItemcd: '품목코드',
        childItemUsage: '수량',
		itemNm: '품명',
		itemSpec: '규격',
		itemUnit: '단위',
        remark: '비고',
    }
	
	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['coordinateCd', 'coordinateNm', 'childItemNm', 'childItemSpec', 'childItemUnit',],
		insertedDisabledCells: [''],
		commonDisabledCells: [''],
		pointerCells: ['childItemcd',  'childItemNm', 'childItemSpec', 'childItemUnit', 'childItemUsage', ],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		alignLeftCells: ['']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<MST3050_R01DetailDto>({
            idKey: 'valueIndex',
            selectedList: selectedMST3050_R01DetailList,
            deletedList: deletedMST3050_R01DetailList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
        selectedMST3050_R01DetailList, deletedMST3050_R01DetailList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
		createCellClassRules,
    ]);


    /**
     * 편집 가능한 컬럼 목록 정의
     * 사용자가 직접 입력/수정할 수 있는 필드들을 지정
     */
    const defaultEditableColumns = ['childItemcd', 'childItemUsage', ];

    /**
     * 기본 상세 테이블 이벤트 핸들러 훅
     * 
     * 주요 기능:
     * - 키다운 네비게이션 (방향키 이동)
     * - 셀 클릭/키다운 이벤트 처리
     * 
     * @param detailList - 상세 데이터 목록
     * @param editableColumns - 편집 가능한 컬럼 목록
     */
    const cellEventHandlers = useDetailTableEvents({
        detailList: MST3050_R01DetailList,
        editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
    });

    const baseColumnDefs: ColDef<MST3050_R01DetailDto>[] = useMemo(() => [
        ...getDefaultRowIndexColumnConfig(),
        { headerName: '로케이션', colId: 'coordinateCd', field: 'coordinateCd', width: 77, editable: false, },
        { headerName: '로케이션명', colId: 'coordinateNm', field: 'coordinateNm', width: 135, editable: false, },
        { headerName: '품목코드', colId: 'childItemcd', field: 'childItemcd', width: 135, editable: true, },
		{ headerName: '품명', colId: 'childItemNm', field: 'childItemNm', width: 135, editable: false, },
        { headerName: '규격', colId: 'childItemSpec', field: 'childItemSpec', width: 135, editable: false, },
        { headerName: '단위', colId: 'childItemUnit', field: 'childItemUnit', width: 135, editable: false, },
        { headerName: '소요량', colId: 'coordinateUsage', field: 'coordinateUsage', width: 135, editable: true, },
		// ...IndexParameterCell(),
    ], [deletedMST3050_R01DetailList, MST3050_R01DetailList]);

    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
    [baseColumnDefs]);

    const columnDefs: ColDef<MST3050_R01DetailDto>[] = useMemo(() => {
        if (detailColumnState === null) {
            return baseColumnDefs;
        }
        
        return GridCustomColumnDefs<MST3050_R01DetailDto>({
            baseColumnDefs,
            columnState: detailColumnState,
            headerNames,
            fallbackHeaderClass: aggridHeaderClass
        });
    }, [baseColumnDefs, detailColumnState, headerNames]);

    const handleRowClick = useRowSelectionHandler({
        dataList: MST3050_R01DetailList,
        idKey: 'valueIndex',
        selectedList: selectedMST3050_R01DetailList,
        deletedList: deletedMST3050_R01DetailList,
        addSelected: addSelectedMST3050_R01DetailList,
        removeSelected: removeSelectedMST3050_R01DetailList,
        clickable, setClickable
    });

	
    // 더블클릭 시에만 품목 코드검색 모달 오픈
    const handleCellDoubleClick = (e: any) => {
        const field = e.colDef.field;
        const data = e.data as MST3050_R01DetailDto;
        if (!data) return;
        if (field === 'childItemcd' || field === 'childItemNm' || field === 'childItemSpec' || field === 'childItemUnit' ) {
            const search = data.childItemcd || '';
            detailTableHandlers.settingCodeSearchModalProps(
                '품목',
                search,
                false,
                'itemCd',
                data,
                data.valueIndex
            );
        }
    };


    const handlePostSort = useCallback((params: PostSortRowsParams<MST3050_R01DetailDto>) => {
        postSortHandler(params, MST3050_R01DetailList, originalMST3050_R01DetailList, setMST3050_R01DetailList);
    }, [MST3050_R01DetailList, originalMST3050_R01DetailList, ]);

    const pinnedBottomRowData = useMemo(() => {

        return [{
            coordinateCd: MST3050_R01DetailList.length.toLocaleString() + "건",
        }];      
    }, [MST3050_R01DetailList, deletedMST3050_R01DetailList,]);

    return (
        <>
            <div className="w-full h-full flex flex-col gap-[4px]">
				{/* selectedDetailMST3050_R01 : {JSON.stringify(editMST3050_R01Detail)}<br /> */}
				
				<GridContextWrapper<MST3050_R01DetailDto>
					gridWrapperRef={gridWrapperRef}
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					enableCellCalculation
					setPaddedGridData={setMST3050_R01DetailList}
					initSelected={initSelectedMST3050_R01DetailList}
					defaultRowData={defaultMST3050_R01DetailRow}
					baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setDetailColumnState}
					localStorageKey={COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Excel<MST3050_R01DetailDto>
						ref={gridRef}
						rowData={paddedGridData}
						getRowId={getRowId}
						columnDefs={columnDefs}
						defaultColDef={cellCssDefaultColDef}

						// ✅ 새로운 훅 구조와 호환
						disableBuiltinCellSelection={true} // 내장 셀 선택 비활성화 (새로운 훅 사용)
						columnStateKey={COLUMN_STATE_KEY} // 자동 컬럼 상태 관리 (localStorage)
						setColumnState={setDetailColumnState}
						getDefaultColumnState={() => defaultColumnState}
						scrollTop={detailScrollTop}
						setScrollTop={setDetailScrollTop}

						// 행 스타일 규칙 (선택/삭제/수정 상태에 따른 색상)
						rowClassRules={createRowClassRules<MST3050_R01DetailDto>({
							idKey: 'valueIndex',
							selectedList: selectedMST3050_R01DetailList,
							deletedList: deletedMST3050_R01DetailList
						})}

						// 이벤트 핸들러들
						onRowClicked={handleRowClick}                                    // 행 클릭 (선택)
						onCellClicked={cellEventHandlers.handleCellClick}               // 셀 클릭 (편집 시작)
						onCellDoubleClicked={handleCellDoubleClick}                     // 셀 더블클릭 (코드 검색)
						onCellKeyDown={cellEventHandlers.handleCellKeyDown}             // 셀 키다운 (네비게이션)
						onCellValueChanged={detailTableHandlers.handleCellChange}
						onCellEditingStopped={detailTableHandlers.handleCellEditingStopped}
						pinnedBottomRowData={pinnedBottomRowData}
						
						// 정렬 후 처리
						onPostSortRows={handlePostSort}
					/>
				</GridContextWrapper>
            </div>
        </>
    )
}
